import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Breadcrumbs,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Home as HomeIcon,
  SportsKabaddi as CoachIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const CoachingDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const coachingFeatures = [
    {
      title: 'Coaching Sessions',
      description: 'Manage one-off coaching sessions with flexible time slot bookings',
      icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
      path: '/coaching/sessions',
      color: 'primary',
      features: [
        'Coach availability sessions',
        'Flexible member booking slots',
        'Guest booking support',
        'Hourly rate pricing',
        'Court reservations'
      ]
    },
    {
      title: 'Coaching Programs',
      description: 'Create and manage recurring coaching programs and clinics',
      icon: <RepeatIcon sx={{ fontSize: 40 }} />,
      path: '/coaching/programs',
      color: 'secondary',
      features: [
        'Recurring program schedules',
        'Full program vs drop-in enrollment',
        'Enrollment management',
        'Waitlist support',
        'Program pricing'
      ]
    }
  ];

  const quickActions = [
    {
      title: 'Create Session',
      description: 'Set up a new coaching session',
      icon: <AddIcon />,
      action: () => navigate('/coaching/sessions?create=true'),
      color: 'primary'
    },
    {
      title: 'Create Program',
      description: 'Start a new coaching program',
      icon: <AddIcon />,
      action: () => navigate('/coaching/programs?create=true'),
      color: 'secondary'
    },
    {
      title: 'View Schedule',
      description: 'See all coaching activities',
      icon: <CalendarIcon />,
      action: () => navigate('/calendar'),
      color: 'info'
    }
  ];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography color="text.primary">Coaching Management</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              <CoachIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Coaching Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage coaching sessions and programs for your club
            </Typography>
          </Box>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={action.action}
                >
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Box sx={{ color: `${action.color}.main`, mb: 1 }}>
                      {action.icon}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Coaching Features */}
        <Grid container spacing={3}>
          {coachingFeatures.map((feature, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: `${feature.color}.main`, mr: 2 }}>
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                        {feature.title}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {feature.description}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Key Features:
                  </Typography>
                  
                  <List dense>
                    {feature.features.map((item, itemIndex) => (
                      <ListItem key={itemIndex} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: `${feature.color}.main`
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={item}
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            color: 'text.secondary'
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={2}>
                      <Button
                        variant="contained"
                        color={feature.color as 'primary' | 'secondary'}
                        onClick={() => navigate(feature.path)}
                        startIcon={<SettingsIcon />}
                      >
                        Manage {feature.title}
                      </Button>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Getting Started */}
        <Paper sx={{ p: 4, mt: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <CoachIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            Getting Started with Coaching Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            Set up coaching sessions for one-on-one or small group lessons, or create ongoing programs 
            for clinics and training courses. Both options support flexible scheduling, member management, 
            and guest access.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<ScheduleIcon />}
              onClick={() => navigate('/coaching/sessions')}
            >
              Start with Sessions
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RepeatIcon />}
              onClick={() => navigate('/coaching/programs')}
            >
              Create Programs
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default CoachingDashboardPage;
